<template>
    <transition name="modal--transition" appear mode="out-in">
    <div
        :class="[
            'modal position--fixed display--flex',
            `modal--${settings.position}`
        ]"
        @click.self="close"
    >
        <div
            :class="[
                'modal__content background--white',
                ( !settings.class ) ? 'modal__content--default' : settings.class
            ]"
        >
            <div
                class="
                    modal__content__header
                    modal--padding
                    display--flex
                    color--white
                "
                :class="`background--${settings.color}`"
            >
                <div class="modal__content__header__label">
                    <slot name="modal__header" />
                </div>
                <span
                    v-if="settings.show.close"
                    class="modal__content__header__close position--relative"
                    :class="executeCloseAction ? 'pointer' : 'disable'"
                    v-on="{
                        ...( executeCloseAction && {
                            click: () => close()
                        })
                    }"
                    aria-hidden="true"
                    :aria-label="$t('accessibility.close')"
                ></span>
            </div>
            <div class="modal__content__body modal--padding">
                <base-form :id="form.settings && form.settings.id || 'formModal'" :class="form.class" v-bind="{ ...form.settings }" >
                    <slot name="modal__body" />
                </base-form>
                <slot name="modal__body__confirm" />
                <div
                    v-if="settings.show.actions"
                    :class="[
                        'modal__controls',
                        { 'display--grid page--single__half': settings.show.infoSection }
                    ]"
                >
                    <div v-if="settings.show.infoSection" class="modal__controls__info">
                        <slot name="modal__info" />
                    </div>
                    <submission-handler :color="settings.color">
                        <base-button
                            :form="form.settings && form.settings.id || 'formModal'"
                            :type="settings.button.type"
                            class="modal__content__button display--block color--white"
                            :class="`background--${settings.color}`"
                            v-html="$t(`global.${settings.button.label}`)"
                            v-bind="{
                                ...( settings.button.id ) && {
                                    id: settings.button.id
                                }
                            }"
                            v-on="{
                                ...( settings.button.action && {
                                    click: () => settings.button.action()
                                })
                            }"
                        />
                    </submission-handler>
                </div>
            </div>
        </div>
    </div>
    </transition>
</template>

<script>
// packages
import { reactive, watch } from '@vue/composition-api';

export default {
    components: {
        BaseForm: () => import( /* webpackChunkName: 'BaseForm' */ '@/components/bundles/layout/forms/BaseForm' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        SubmissionHandler: () => import(
            /* webpackChunkName: 'SubmissionHandler' */
            '@/components/bundles/layout/SubmissionHandler/SubmissionHandler'
        )
    },
    props: {
        form: Object,
        customSettings: Object,
        executeCloseAction: {
            type: Boolean,
            default: true
        },
        preventReset: {
            type: Boolean,
            default: false
        }
    },
    setup ( props, { emit }) {
        // obtenção das custom configurações
        const { show, button, ...rest } = props.customSettings;

        // definição das configurações da modal
        const settings = reactive({
            color: 'red',
            position: 'center',
            class: null,
            ...rest,
            button: {
                label: 'submit',
                type: 'submit',
                action: null,
                ...button
            },
            show: {
                close: true,
                infoSection: true,
                actions: true,
                ...show || {}
            }
        });

        const close = () => {
            if ( !props.executeCloseAction )
                return;

            emit( 'close' );
        };

        /**
         * [listener]
         * Está atento aos props que tratam da visibilidade de
         * alguns elementos do modal. Se algum valor mudar,
         * serão atualizadas as settings.
         *
         * @since 1.2.3
         * @category listener
         * @autor Jorge Monteiro
         *
        */
        watch( () => props.customSettings.show,
            newShowPermissions => {
                settings.show = {
                    ...settings.show,
                    ...newShowPermissions
                };
            });

        return { settings, close };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $contentClass: '.modal__content';
    $buttonWidth: calc( 50% - 10px );

    // =============================================================================
    // Animations
    // =============================================================================
    @include keyframes ( scale-up ) {
        0% {
            @include transform( scale( 0 ) );
        }
        60% {
            @include transform( scale( 1.2 ) );
        }
        100% {
            @include transform( scale( 1 ) );
        }
    }

    @include keyframes ( scale-down ) {
        0% {
            @include transform( scale( 1 ) );
        }
        40% {
            @include transform( scale( 1.1 ) );
        }
        100% {
            @include transform( scale( 0 ) );
        }
    }

    // =============================================================================
    // Style
    // =============================================================================
    .modal {
        @include transform-position( 0, 0, 0, 0 );
        justify-content: center;
        background: rgba( $color: $color-grey, $alpha: .65 );
        z-index: 100;  // TODO: devido a página de guião, caso houver refactor volta a 7.
        &--padding {
            padding: 20px 40px;
        }
        &--baseline {
            align-items: baseline;
            padding-top: 75px;
        }
        &--center {
            align-items: center;
        }
        &--transition {
            &-enter,
            &-leave-active  {
                opacity: 0;
                will-change: opacity;
                & #{$contentClass} {
                    @include animation( scale-down .4s cubic-bezier( .0, .0, .2, 1 ) )
                }
            }
            &-enter-active,
            &-leave-active   {
                @include transition( opacity .3s ease-in-out );
            }
            &-enter-active {
                & #{$contentClass} {
                    @include animation( scale-up .4s cubic-bezier( .0, .0, .2, 1 ) )
                }
            }
        }
        &__form {
            &--action {
                align-items: center;
                justify-content: center;
                height: 50px;
                text-align: center;
                @include typography-specs( 18px, 24px );
            }
        }
        &__content {
            width: 100%;
            @include rounded-corner( 10px );
            &--default {
                max-width: 715px;
            }
            &__header {
                justify-content: space-between;
                @include rounded-corner( 10px 10px 0 0 );
                &__label {
                    &::first-letter {
                        text-transform: capitalize;
                    }
                }
                &__close {
                    @include dimensions( 21px, 21px );
                    &::before, &::after {
                        @include pseudo();
                        top: 12px;
                        @include dimensions( 21px, 2px );
                        background: $color-white;
                    }
                    &::before {
                        @include transform( rotate( -45deg ) );
                    }
                    &::after {
                        @include transform( rotate( 45deg ) );
                    }
                }
            }
            &__body {
                padding-bottom: 40px;
            }
            &__button {
                &--negative {
                    color: $color-grey;
                }
            }
        }
        &__controls {
            margin-top: 30px;
            align-items: center;
            &__info {
                @include typography-specs( 16px, 24px );
            }
        }
        @include responsive( max-height, 860px ) {
            & {
                align-items: baseline;
                padding: 80px 0;
                overflow-y: auto;
                &::-webkit-scrollbar {
                    width: 9px;  /* for vertical scrollbars */
                }
                &::-webkit-scrollbar-track {
                    background: $color-white;
                    @include rounded-corner( 9px );
                }
                &::-webkit-scrollbar-thumb {
                    background: $color-blue;
                    @include rounded-corner( 9px );
                }
            }
        }
    }
</style>